<template>
  <div class="d-flex studio-community-messages">
    <div
      class="studio-community-chat-rooms"
      :class="{ 'chatroom-selected': isChatSelected }"
    >
      <div class="toolbar d-flex align-items-center justify-content-between">
        <h4 class="ml-4">{{ $t('COMMUNITY.LABEL.MESSAGES') }}</h4>
        <HMButton color="outline-gray" class="mr-4" @click="createNewChat">
          <font-awesome-icon
            :icon="['far', 'plus']"
            class="icon c-brand-color"
          />
        </HMButton>
      </div>
      <div class="ml-4 mr-4 mt-4">
        <HMSearch v-model="search" @input="searchMessages" class="mb-4" />
        <div>
          <div
            class="pt-1 pb-2 messages-container"
            v-infinite-scroll="loadMoreChats"
            infinite-scroll-disabled="inProcess"
            infinite-scroll-distance="10"
            infinite-scroll-throttle-delay="500"
          >
            <div
              v-for="chatroom in sortedChatrooms"
              :key="chatroom.id"
              class="pb-2"
              @click="openChatRoom(chatroom.id, chatroom)"
            >
              <StudioCommunityChatRoomItem
                :id="`chatroom-${chatroom.id}`"
                :chatroom="chatroom"
                :isActive="chatroom == activeChatRoom"
              />
            </div>
            <div class="text-center" v-if="inProcess">
              <HMSpinner></HMSpinner>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="chat-container position-relative"
      :class="{ 'chatroom-selected': isChatSelected }"
    >
      <div
        class="mb-2 mb-lg-4 toolbar d-flex align-items-center justify-content-between pr-4"
      >
        <div class="d-flex align-items-center w-100" v-if="hasChatrooms">
          <div class="user-search-container align-items-center d-flex pl-2">
            <HMImagesWithLinkStack :images="chatRoomMemberPhotos" size="36px" />
            <span class="h5 px-3 mb-0">{{ roomName }}</span>
          </div>
          <HMButton
            v-if="isGroupChatRoom"
            color="outline-gray"
            class="mr-4"
            @click="editChatRoom"
          >
            <i class="fal fa-cog"></i>
          </HMButton>
          <div @click="openRecordingModal" class="pointer">
            <i class="fa fa-microphone"></i>
          </div>
        </div>
      </div>
      <HMPublicChat
        v-if="showChat"
        class="chat-container col-sm-12 m-0 p-0 h-100 pr-lg-4"
        :userId="user.community_profile_id"
        :userName="user.user_full_name"
        :userImage="userProfileData.image"
        :userToken="userProfileData.chat_token"
        :channelId="roomId"
        @newMessage="chatNewMessage"
        @onChannelConnected="onChannelConnected"
        @onAddedToChannel="onAddedToChannel"
        @onRemovedFromChannel="onRemovedFromChannel"
        @onNewMessage="onNewMessage"
        @onMarkRead="onMarkRead"
        :key="roomId"
      />
      <div
        v-if="!hasChatrooms && !inProcess"
        class="d-flex justify-content-center"
      >
        {{ $t('COMMUNITY.TEXT.YOU_DONT_HAVE_CHATS') }}
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import HMPublicChat from '@/web/components/chat/HMPublicChat';
import AudioRecordingModal from '@/web/components/community/AudioRecordingModal';
import { errorToMessage } from '@/core/utils/errors';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import StudioCommunityChatRoomItem from '@/web/views/shop/StudioCommunityChatRoomItem';
import CommunityChatRoomModal from '@/web/views/admin/community/CommunityChatRoomModal';
import CommunityNewChatModal from '@/web/views/admin/community/CommunityNewChatModal';
import CommunityNewDMChatModal from '@/web/views/admin/community/CommunityNewDMChatModal';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import { isTouchDevice } from '@/web/utils/detect-device';
import { getAvatarURL } from '@/core/utils/utils';
import { cloneDeep } from 'lodash';
import RefreshData from '@/web/mixins/refreshData';

export default Vue.extend({
  name: 'StudioCommunityChat',
  mixins: [RefreshData],
  components: {
    HMPublicChat,
    StudioCommunityChatRoomItem,
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
      user: 'user/user',
      currentStudioId: 'studio/currentStudioId',
      studioCommunityProfileId: 'studio/communityProfileId',
      chatClient: 'community/chatClient',
      isLoggedIn: 'user/isLoggedIn',
    }),
    isChatSelected() {
      // this only checks is user manually selected the chat already
      // if no chat is selected, we still load and manually select
      // the first chat in the list, but this property helps us on
      // small devices, where we ask the user to select the chat room first
      return this.$route.query.room_id !== undefined;
    },
    showChat() {
      return this.user && this.userProfileData.chat_token && this.roomId;
    },
    activeChatRoomName() {
      if (!this.activeChatRoom) return '';
      return this.activeChatRoom.room_name;
    },
    roomName() {
      return this.activeChatRoomName || this.getMembersNames;
    },
    chatRoomId() {
      return this.activeChatRoom?.id;
    },
    roomId() {
      return this.chatRoomId || this.sortedChatrooms?.[0]?.id;
    },
    hasChatrooms() {
      return this.chatrooms?.length;
    },
    chatRoomPhoto() {
      let image = null;
      if (this.activeChatRoom.image) {
        image = this.activeChatRoom.image;
      } else if (this.activeChatRoom.is_private) {
        const otherMember =
          this.activeChatRoom?.members?.find(
            member => member.id != this.user.community_profile_id
          ) || {};
        image = otherMember.image;
      } else {
        const adminMember =
          this.activeChatRoom?.members?.find(member => member.is_admin) || {};
        image = adminMember.image;
      }
      return { image };
    },
    chatRoomMemberPhotos() {
      let images = [];
      if (this.activeChatRoom.image) {
        images.push({ url: this.activeChatRoom.image });
      } else if (this.activeChatRoom.is_private) {
        const otherMember =
          this.activeChatRoom?.members?.find(
            member => member.id != this.user.community_profile_id
          ) || {};
        images.push(this.memberToImage(otherMember));
      } else {
        const membersImages = this.getLimitedMembers.map(member => {
          return this.memberToImage(member);
        });
        images = membersImages;
      }
      return images;
    },
    sortedChatrooms() {
      return [...this.chatrooms].sort(
        (a, b) => new Date(b.updated) - new Date(a.updated)
      );
    },
    getMembersCount() {
      if (!this.activeChatRoom?.members) return 0;
      return this.activeChatRoom.members.length;
    },
    getLimitedMembers() {
      if (!this.activeChatRoom?.members) return [];
      return [...this.activeChatRoom.members].splice(0, 5);
    },
    getMembersNames() {
      let members = this.getLimitedMembers;
      if (this.activeChatRoom?.is_private) {
        members = members.filter(
          member => member.id !== this.userProfileData.id
        );
      }
      const hasOverFiveMembers = this.getMembersCount > 5;
      const names = `${(members || [])
        .map(member => member.first_name)
        .join(', ')}${hasOverFiveMembers ? ', ...' : ''}`;
      return names;
    },
    isGroupChatRoom() {
      return !this.activeChatRoom?.is_private;
    },
  },
  data() {
    return {
      channel: null,
      isChatClosed: true,
      newMessagesCount: 0,
      userProfileData: {},

      chatrooms: [],
      chatroomsNext: null,
      inProcess: true,
      search: '',
      searchById: null,

      users: [],
      page: 1,
      newChatName: null,
      activeChatRoom: {},
    };
  },
  methods: {
    openRecordingModal() {
      this.$modal.show(AudioRecordingModal, {
        onRecordingAvailable: this.sendAudioToChat,
      });
    },
    memberToImage(member) {
      const name = `${member.first_name} ${member.last_name}`;
      return {
        url:
          member.image ||
          getAvatarURL(name, this.studio.primary_color, 'white', 20, '36px'),
        tooltip: name,
        profile: member.id,
      };
    },
    async sendAudioToChat(file) {
      if (this.channel) {
        const response = await this.channel.sendFile(file);

        const message = await this.channel.sendMessage({
          attachments: [
            {
              type: 'audio',
              asset_url: response.file,
              image_url: 'https://dv05ui3l6dkej.cloudfront.net/waveform.png',
              title: `Message from ${this.user.community_first_name}`,
            },
          ],
        });
      }
    },
    playSound() {
      const audioFile = '/assets/audio/new_message.mp3';
      const audio = document.createElement('audio');
      audio.style.display = 'none';
      audio.src = audioFile;
      audio.autoplay = true;
      audio.onended = function () {
        audio.remove(); //Remove when played.
      };
      document.body.appendChild(audio);
    },
    onChannelConnected(channel) {
      this.channel = channel;
    },
    onAddedToChannel(event) {
      this.playSound();
      this.loadChatRooms();
    },
    onRemovedFromChannel(event) {
      this.loadChatRooms();
    },
    async onNewMessage(event) {
      this.playSound();
      this.checkRoomUnreadCount(event.channel_id);
    },
    async onMarkRead(event) {
      this.checkRoomUnreadCount(event.channel_id);
    },
    async queryStreamChanels() {
      const filter = {
        type: 'messaging',
        members: { $in: [this.userProfileData.id] },
      };
      const sort = { last_message_at: -1 };
      const channels = await this.chatClient.queryChannels(filter, sort, {
        watch: false,
        limit: 10,
        offset: this.page - 1,
      });
      this.onChannelsLoaded(channels);
    },
    onChannelsLoaded(channels) {
      // channels is a list of channels from stream
      channels.forEach(async channel => {
        const chatRoom = this.getChatRoomById(this.chatrooms, channel.id);
        if (chatRoom) {
          Vue.set(chatRoom, 'unreadCount', channel.state.unreadCount);
          Vue.set(
            chatRoom,
            'updated',
            channel.state.last_message_at || channel.data.created_at
          );
        }
      });
    },
    async checkRoomUnreadCount(roomId) {
      const chatRoom = this.getChatRoomById(this.chatrooms, roomId);
      const filter = { id: { $in: [roomId] } };
      const channels = await this.chatClient.queryChannels(
        filter,
        {},
        { watch: false }
      );
      if (channels.length > 0) {
        const foundChannel = channels[0];
        Vue.set(chatRoom, 'unreadCount', foundChannel.state.unreadCount);
        Vue.set(
          chatRoom,
          'updated',
          foundChannel.state.last_message_at || foundChannel.data.created_at
        );
      }
    },
    toggleChat() {
      this.isChatClosed = !this.isChatClosed;
      if (!this.isChatClosed) {
        this.newMessagesCount = 0;
      }
    },
    chatNewMessage() {
      if (this.isChatClosed) {
        this.newMessagesCount += 1;
      }
    },
    async loadUserProfileData() {
      try {
        this.userProfileData = await this.$store.dispatch(
          'community/loadStudentCommunityProfileData'
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            `${this.$t('COMMUNITY.TEXT.PROFILE_LOADING_ERROR')}: ${error}`
          ),
          duration: -1,
        });
      }
    },
    async openChatRoom(chatRoomId, chatroom, rooms, preventNavigation) {
      this.activeChatRoom = chatroom || this.getChatRoomById(rooms, chatRoomId);
      if (preventNavigation) {
        return;
      }
      if (this.$route.query.room_id === chatRoomId) {
        // prevent navigating to the same room
        return;
      }
      const query = { ...this.$route.query, room_id: chatRoomId };
      if (isTouchDevice()) {
        this.$router.push({ query });
      } else {
        this.$router.replace({ query });
      }
    },
    getChatRoomById(rooms, roomId) {
      return rooms.find(room => room.id == roomId) || {};
    },
    async loadChatRooms(loadMore) {
      const q = this.search;
      try {
        this.inProcess = true;
        const rooms = await this.$store.dispatch('community/loadChatRooms', {
          studioURL: getStudioURL(this.$route),
          query: q,
          id: this.searchById,
          page: this.page,
        });
        this.searchById = null;
        if (loadMore) {
          this.chatrooms = [...this.chatrooms, ...rooms.results];
        } else {
          this.chatrooms = rooms.results;
        }
        this.chatroomsNext = rooms.next;
        await this.queryStreamChanels();
        const roomId = this.$route.query.room_id;
        if (roomId) {
          const roomInTheList = this.getChatRoomById(this.chatrooms, roomId);
          if (!roomInTheList.id) {
            const searchedRoom = await this.$store.dispatch(
              'community/getPublicChatRoom',
              {
                studioURL: getStudioURL(this.$route),
                roomId,
              }
            );
            this.chatrooms = [searchedRoom, ...this.chatrooms];
            const roomElement = document.getElementById(`chatroom-${roomId}`);
            if (roomElement) roomElement.scrollIntoView();
          }
          this.openChatRoom(roomId, null, this.chatrooms);
        } else {
          this.openChatRoom(
            this.sortedChatrooms?.[0]?.id,
            null,
            this.chatrooms,
            true
          );
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async createPublicChatRoom(chatRoom) {
      try {
        this.inProcess = true;
        const newChat = await this.$store.dispatch(
          'community/createPublicChatRoom',
          {
            studioURL: getStudioURL(this.$route),
            payload: formatObjectForBackend(chatRoom),
          }
        );

        this.chatrooms.unshift(newChat);
        this.openChatRoom(newChat.id, newChat);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    async editPublicChatRoom(chatRoom) {
      try {
        this.inProcess = true;
        const updatedChatRoom = await this.$store.dispatch(
          'community/editPublicChatRoom',
          {
            studioURL: getStudioURL(this.$route),
            roomId: this.activeChatRoom.id,
            payload: formatObjectForBackend(chatRoom),
          }
        );
        this.activeChatRoom = updatedChatRoom;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      } finally {
        this.inProcess = false;
      }
    },
    searchMessages() {
      // reset page and chatrooms on search
      this.page = 1;
      this.chatrooms = [];
      this.loadChatRooms(false);
    },
    async loadMoreChats() {
      if (!this.chatroomsNext) return;
      this.page = this.page + 1;
      this.loadChatRooms(true);
    },
    async onChatRoomCreate(chatRoom) {
      await this.createPublicChatRoom(chatRoom);
      this.loadChatRooms(false);
    },
    async onChatRoomEdit(chatRoom) {
      await this.editPublicChatRoom(chatRoom);
      this.loadChatRooms(false);
    },
    createNewChat() {
      this.$modal.show(
        CommunityNewChatModal,
        {
          onSendNewDirectMessage: this.createOrGetDMChatRoom,
          onSendNewGroupMessage: this.createNewChatRoom,
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    async startDMChat(member) {
      console.log('starting DM with ', member);
      try {
        const privateRoom = await this.$store.dispatch(
          'community/createPrivateChatRoom',
          {
            studioURL: getStudioURL(this.$route),
            member: member.id,
          }
        );
        await this.loadChatRooms();
        this.openChatRoom(privateRoom.id, null, this.chatrooms);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    createOrGetDMChatRoom() {
      this.$modal.show(
        CommunityNewDMChatModal,
        {
          confirm: this.startDMChat,
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    createNewChatRoom() {
      this.$modal.show(
        CommunityChatRoomModal,
        {
          confirm: this.onChatRoomCreate,
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    editChatRoom() {
      this.$modal.show(
        CommunityChatRoomModal,
        {
          confirm: this.onChatRoomEdit,
          chatRoom: cloneDeep(this.activeChatRoom),
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    async connectChatUser() {
      await this.chatClient.disconnectUser();

      const id = this.userProfileData.id;
      const name = `${this.userProfileData.first_name} ${this.userProfileData.last_name}`;
      const image =
        this.userProfileData.image ||
        getAvatarURL(name, this.studio.primary_color, 'white', 14, '25px');
      const member = await this.chatClient.connectUser(
        { id, name, image },
        this.userProfileData.chat_token
      );
      this.$store.dispatch(
        'community/setNumberOfChatNotifications',
        // @ts-ignore
        member.me.unread_channels
      );
    },
    async loadData() {
      if (!this.isLoggedIn) return;
      await this.loadUserProfileData();
      await this.connectChatUser();
      this.searchById = this.$route.query.room_id;
      await this.loadChatRooms();
    },
  },
  async mounted() {
    await this.loadData();
  },
  watch: {
    search: {
      handler: function () {
        this.searchById = null;
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.chat-rooms {
  width: 30%;
}

.chat-container {
  z-index: 0 !important;
  height: 80vh;
  width: 70%;
  border-left: 1px solid $light-gray;

  @include media-breakpoint-down(md) {
    width: 0;
    overflow: hidden;
    &.chatroom-selected {
      width: 100%;
      height: 93vh;
    }
  }
}

.toolbar {
  width: 100%;
  min-height: 90px;
  box-shadow: 0 2px 10px rgba(44, 39, 56, 0.08);

  @include media-breakpoint-down(md) {
    min-height: 60px;
  }
}

.studio-community-messages {
  border-top: 1px solid $light-gray;
  border-bottom: 1px solid $light-gray;
}

.studio-community-chat-rooms {
  width: 30%;

  @include media-breakpoint-down(md) {
    width: 100%;
    overflow: hidden;
    &.chatroom-selected {
      width: 0;
    }
  }
}

.users-selector {
  flex: 1;
}

.user-search-container {
  flex: 1;
}

::v-deep {
  .str-chat {
    max-height: 80% !important;
  }

  #stream-chat-react {
    border: none;
    width: 100%;
  }
}

.messages-container {
  height: 60vh;
  overflow: auto;

  &::-webkit-scrollbar {
    width: 6px;
    background-color: $light-gray;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    background-color: white;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: $light-gray;
  }
}

.room__icon {
  height: 48px;
  width: 48px;
}
</style>
